<script setup>
import {onMounted, onUnmounted, watch} from 'vue';
import {useUser} from "@/Store/User.js";
import {getLocalStorageItem} from "@/helper.js";

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    maxWidth: {
        type: String,
        default: '2xl',
    },
    closeable: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['close']);

watch(() => props.show, () => {
    if (document === undefined) {
        return;
    }
    if (props.show) {
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = null;
    }
});

const close = () => {
    if (props.closeable) {
        emit('close');
    }
};

const closeOnEscape = (e) => {
    if (e.key === 'Escape' && props.show) {
        close();
    }
};

onMounted(() => document !== undefined && document.addEventListener('keydown', closeOnEscape));

onUnmounted(() => {
    if (document === undefined) {
        return;
    }
    document.removeEventListener('keydown', closeOnEscape);
    document.body.style.overflow = null;
});

const storeUser = useUser();

const theme = getLocalStorageItem('color-scheme');
if (theme) {
    storeUser.theme = theme;
}

</script>

<template>
    <teleport to="body">
        <transition leave-active-class="duration-200">
            <div v-show="show" :class="storeUser.theme" class="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-0 z-50"
                 scroll-region>
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="show" class="fixed inset-0 transform transition-all" @click="close">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"/>
                    </div>
                </transition>

                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div v-show="show"
                         class="max-w-[340px] mb-6 bg-white dark:bg-dark-blue rounded-lg overflow-hidden shadow-xl transform transition-all sm:w-full mx-auto">
                        <slot v-if="show"/>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>
